import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import {
  LifeBuoy, BookOpen, Activity, Briefcase, Handshake,
  Clock, MessageSquare, Search, FileText, Server, ShieldCheck,
  DollarSign, Users, Globe, Building2, ArrowRight,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type Block = { icon: LucideIcon; title: string; body: string };
type ChildPage = {
  title: string;
  eyebrow: string;
  sub: string;
  metaDesc: string;
  intro: string;
  blocks: Block[];
  cta?: { label: string; to: string };
};

const CHILDREN: Record<string, ChildPage> = {
  "support-center": {
    eyebrow: "Support · Help",
    title: "Support Center",
    sub: "One place to open, track and resolve tickets.",
    metaDesc: "The CoderLity Support Center — open tickets, track requests and get help from senior engineers fast.",
    intro:
      "The Support Center is your hub for everything help-related. Open tickets, track status, attach logs and chat directly with the engineer assigned to your case — not a tier-one bot.",
    blocks: [
      { icon: LifeBuoy, title: "Open a ticket", body: "Submit issues with priority, category and attachments — routed to the right team instantly." },
      { icon: Clock, title: "Live status", body: "Track ticket status, SLA timers and last response in your dashboard." },
      { icon: MessageSquare, title: "Direct replies", body: "Two-way thread with the engineer on your case, not a generic queue." },
      { icon: ShieldCheck, title: "Priority SLAs", body: "Urgent issues acknowledged in under 30 minutes for enterprise plans." },
    ],
    cta: { label: "Open a ticket", to: "/support" },
  },
  "knowledge-base": {
    eyebrow: "Support · Help",
    title: "Knowledge Base",
    sub: "Searchable guides, how-tos and troubleshooting.",
    metaDesc: "CoderLity Knowledge Base — searchable how-tos, troubleshooting and best-practice articles for every product.",
    intro:
      "Hundreds of articles covering setup, configuration, troubleshooting and best practices across all our products. Versioned, search-first and maintained by the engineers who built the features.",
    blocks: [
      { icon: Search, title: "Fast search", body: "Full-text search across every article, with filters by product and version." },
      { icon: FileText, title: "Step-by-step guides", body: "Screenshots, code samples and copy-paste commands that actually work." },
      { icon: BookOpen, title: "Best practices", body: "Opinionated recommendations from the team running production deployments." },
      { icon: ShieldCheck, title: "Always current", body: "Articles versioned per release — old docs stay accessible, new ones surface first." },
    ],
    cta: { label: "Browse the knowledge base", to: "/kb" },
  },
  "system-status": {
    eyebrow: "Support · Help",
    title: "System Status",
    sub: "Real-time uptime and incident history.",
    metaDesc: "Real-time CoderLity system status — uptime, incidents and scheduled maintenance for every service.",
    intro:
      "Transparent, real-time status for every service we operate. Subscribe to incident updates by email, SMS or webhook and see the full historical record going back years.",
    blocks: [
      { icon: Activity, title: "Live uptime", body: "Per-service status updated every 30 seconds, region by region." },
      { icon: Server, title: "Incident history", body: "Full post-mortems for every incident, with root cause and remediation." },
      { icon: Clock, title: "Scheduled maintenance", body: "Advance notice for planned work — opt into windows that fit your team." },
      { icon: ShieldCheck, title: "Subscribe", body: "Email, SMS, RSS and webhook subscriptions for the services you care about." },
    ],
  },
  "contact-sales": {
    eyebrow: "Support · Contact",
    title: "Contact Sales",
    sub: "Talk to a human about your project.",
    metaDesc: "Contact CoderLity sales — custom pricing, enterprise plans, security reviews and onboarding for serious teams.",
    intro:
      "No forms that go nowhere. Tell us about your project and a senior account lead — not an SDR — gets back to you within one business day with concrete next steps.",
    blocks: [
      { icon: DollarSign, title: "Custom pricing", body: "Volume, multi-year and bundled pricing for teams above the standard tier." },
      { icon: Building2, title: "Enterprise plans", body: "SSO, dedicated infrastructure, custom SLAs and procurement-friendly contracts." },
      { icon: ShieldCheck, title: "Security review", body: "SOC 2 reports, security questionnaires and architecture deep dives on request." },
      { icon: Users, title: "Onboarding", body: "Dedicated onboarding engineer for the first 60 days on enterprise plans." },
    ],
    cta: { label: "Talk to sales", to: "/contact" },
  },
  "partnerships": {
    eyebrow: "Support · Contact",
    title: "Partnerships",
    sub: "Build with us — agencies, resellers and tech partners.",
    metaDesc: "CoderLity partnership program — agency, reseller and technology partnerships with shared revenue and co-marketing.",
    intro:
      "We work with agencies, resellers and technology partners worldwide. Three tiers, transparent revenue share, real co-marketing — and a partner manager who picks up the phone.",
    blocks: [
      { icon: Handshake, title: "Agency program", body: "White-label hosting, infrastructure and managed services for client work." },
      { icon: Briefcase, title: "Reseller program", body: "Tiered margins, deal registration and dedicated reseller support." },
      { icon: Globe, title: "Technology partners", body: "Joint integrations, co-engineering and listed presence in our marketplace." },
      { icon: Users, title: "Co-marketing", body: "Case studies, webinars and conference appearances — real budget, real reach." },
    ],
    cta: { label: "Become a partner", to: "/contact" },
  },
};

export const Route = createFileRoute("/support/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/support/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: SupportChild,
});

function SupportChild() {
  const { item } = Route.useLoaderData();
  return (
    <PageShell>
      <PageHero eyebrow={item.eyebrow} title={item.title} sub={item.sub} />
      <section className="py-12">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <p className="text-lg leading-relaxed text-muted-foreground">{item.intro}</p>
        </div>
      </section>
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {item.blocks.map((b: Block) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-brand text-white shadow-glow">
                <b.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 text-lg font-semibold">{b.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>
      </section>
      {item.cta && (
        <section className="py-12">
          <div className="mx-auto max-w-4xl px-4 lg:px-8">
            <Link
              to={item.cta.to}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-brand px-6 py-3 text-sm font-semibold text-white shadow-glow"
            >
              {item.cta.label} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}
      <CTABanner />
    </PageShell>
  );
}
